import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid, LineChart, Line,
} from "recharts";
import {
  ArrowLeft, FileText, Box, MapPin, Layers, Cpu, Flame, Sparkles, Wrench,
  Cog, Factory, ClipboardCheck, AlertTriangle, Ruler, Info, TrendingDown,
  Scale, RefreshCw, FileSpreadsheet,
} from "lucide-react";
import { Header } from "@/components/Header";
import { AnalyzingOverlay } from "@/components/AnalyzingOverlay";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { fetchAnalysis, recostRegion, costingXlsxUrl, reportPdfUrl, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ModelViewer } from "@/components/ModelViewer";

const CHART_COLORS = [
  "hsl(212 100% 45%)", "hsl(160 60% 40%)", "hsl(30 90% 52%)",
  "hsl(265 65% 58%)", "hsl(340 75% 52%)", "hsl(200 20% 55%)",
];

const confBadge = (c) =>
  c === "high" ? "bg-success/15 text-success border-success/30"
  : c === "low" ? "bg-destructive/10 text-destructive border-destructive/30"
  : "bg-chart-3/15 text-chart-3 border-chart-3/30";

const Section = ({ icon: Icon, title, children, testid, delay = 0 }) => (
  <motion.section
    data-testid={testid}
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay }}
    className="rounded-sm border border-border bg-card p-6 sm:p-7"
  >
    <div className="mb-5 flex items-center gap-2.5">
      <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-primary/10 text-primary">
        <Icon className="h-4 w-4" />
      </div>
      <h2 className="font-title text-lg font-semibold tracking-tight">{title}</h2>
    </div>
    {children}
  </motion.section>
);

const Spec = ({ label, value }) => (
  <div className="rounded-sm border border-border bg-muted/30 p-3">
    <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
    <div className="mt-1 font-title text-sm font-semibold">{value || "—"}</div>
  </div>
);

export default function Analysis() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, setShowLogin, setShowUpgrade } = useAuth();
  const [rec, setRec] = useState(null);
  const [err, setErr] = useState(false);
  const [recosting, setRecosting] = useState("");
  const [clientName, setClientName] = useState("");

  const doRecost = async (pincode, region) => {
    if (!pincode) return;
    if (!user) {
      toast.info("Sign in to re-cost this region — 3 free estimates.");
      setShowLogin(true);
      return;
    }
    if (!user.is_owner && !user.unlimited && user.remaining <= 0) {
      setShowUpgrade(true);
      return;
    }
    setRecosting(pincode);
    try {
      const d = await recostRegion(rec.id, pincode);
      toast.success(`Re-costing for ${region || pincode}…`);
      navigate(`/analysis/${d.id}`);
    } catch (e) {
      setRecosting("");
      if (e?.response?.status === 402) setShowUpgrade(true);
      else toast.error(e?.response?.data?.detail || "Could not re-cost this region.");
    }
  };

  useEffect(() => {
    let alive = true;
    let timer;
    const poll = async () => {
      try {
        const data = await fetchAnalysis(id);
        if (!alive) return;
        setRec(data);
        if (data.status === "processing") timer = setTimeout(poll, 3000);
      } catch {
        if (alive) setErr(true);
      }
    };
    poll();
    return () => { alive = false; clearTimeout(timer); };
  }, [id]);

  if (err) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h1 className="font-display text-2xl font-bold">Analysis not found</h1>
          <Button className="mt-6 rounded-sm" onClick={() => navigate("/")}>Back home</Button>
        </div>
      </div>
    );
  }

  if (!rec) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div className="flex items-center gap-3 text-muted-foreground">
            <Cog className="h-5 w-5 animate-spin" />
            <span className="font-mono text-sm">Loading report…</span>
          </div>
        </div>
      </div>
    );
  }

  if (rec.status === "processing") {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <AnalyzingOverlay />
      </div>
    );
  }

  if (rec.status === "error") {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="mx-auto max-w-3xl px-6 py-24 text-center" data-testid="analysis-error">
          <AlertTriangle className="mx-auto mb-4 h-10 w-10 text-destructive" />
          <h1 className="font-display text-2xl font-bold">Analysis could not be completed</h1>
          <p className="mt-2 text-sm text-muted-foreground">{rec.error || "Something went wrong while analysing this drawing."}</p>
          <Button className="mt-6 rounded-sm" onClick={() => navigate("/")}>Try another drawing</Button>
        </div>
      </div>
    );
  }

  const a = rec.analysis || {};
  const ce = a.cost_estimate || {};
  const donut = (ce.breakdown || []).map((b, i) => ({
    name: b.category,
    value: Math.round(((b.min || 0) + (b.max || 0)) / 2),
    percent: b.percent,
    color: CHART_COLORS[i % CHART_COLORS.length],
  }));
  const regional = (ce.regional_comparison || []).map((r) => ({
    name: r.region,
    min: r.per_unit_min,
    max: r.per_unit_max,
  }));
  const rb = ce.rate_basis || {};
  const hasRateBasis = rb.part_weight_kg || rb.material_rate_inr_per_kg || rb.labour_rate_inr_per_hr || rb.machine_hour_rate_inr;
  const sens = (ce.batch_sensitivity || []).map((s) => ({
    label: s.label || String(s.batch_qty || ""),
    batch_qty: s.batch_qty,
    min: s.per_unit_min,
    max: s.per_unit_max,
    mid: Math.round(((s.per_unit_min || 0) + (s.per_unit_max || 0)) / 2),
  }));

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Title bar */}
      <div className="border-b border-border bg-card/50">
        <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
          <button
            data-testid="back-button"
            onClick={() => navigate("/")}
            className="mb-4 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:text-primary"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 data-testid="part-title" className="font-display text-3xl font-black tracking-tight sm:text-4xl">
                {a.part_name || rec.pdf_filename}
              </h1>
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">{a.summary}</p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-2.5">
              <Badge className={`rounded-sm border font-mono text-[11px] uppercase ${confBadge(a.confidence)}`}>
                {a.confidence || "medium"} confidence
              </Badge>
              <input
                data-testid="pdf-client-input"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Prepared for (client)…"
                className="h-8 w-52 rounded-sm border border-border bg-card px-2.5 text-xs outline-none focus:border-primary/50"
              />
              <div className="flex items-center gap-2">
                <a
                  href={reportPdfUrl(rec.id, clientName)}
                  data-testid="download-pdf-button"
                  className="inline-flex h-9 items-center gap-2 rounded-sm border border-border bg-card px-3 text-sm font-medium transition-colors hover:border-primary/50 hover:text-primary"
                >
                  <FileText className="h-4 w-4" /> Download PDF Summary
                </a>
                <a
                  href={costingXlsxUrl(rec.id)}
                  data-testid="download-excel-button"
                  className="inline-flex h-9 items-center gap-2 rounded-sm border border-border bg-card px-3 text-sm font-medium transition-colors hover:border-primary/50 hover:text-primary"
                >
                  <FileSpreadsheet className="h-4 w-4" /> Download Excel Cost Break Up
                </a>
              </div>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-2 font-mono text-xs">
            <span className="inline-flex items-center gap-1.5 rounded-sm bg-secondary px-2.5 py-1 text-secondary-foreground">
              <FileText className="h-3.5 w-3.5" /> {rec.pdf_filename}
            </span>
            {rec.model_3d_filename && (
              <span className="inline-flex items-center gap-1.5 rounded-sm bg-secondary px-2.5 py-1 text-secondary-foreground">
                <Box className="h-3.5 w-3.5" /> {rec.model_3d_filename}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 rounded-sm bg-secondary px-2.5 py-1 text-secondary-foreground">
              <MapPin className="h-3.5 w-3.5" /> {ce.region ? `${ce.region} · ` : ""}{rec.pincode}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-sm bg-secondary px-2.5 py-1 text-secondary-foreground">
              <Layers className="h-3.5 w-3.5" /> {rec.quantity}/yr
              {ce.batch_size ? <span className="text-muted-foreground">· batch {ce.batch_size}</span> : null}
            </span>
            {rec.geometry?.bbox_mm && (
              <span className="inline-flex items-center gap-1.5 rounded-sm bg-secondary px-2.5 py-1 text-secondary-foreground" data-testid="geometry-chip">
                <Ruler className="h-3.5 w-3.5" /> {rec.geometry.bbox_mm[0]}×{rec.geometry.bbox_mm[1]}×{rec.geometry.bbox_mm[2]} mm
                <span className="text-muted-foreground">· ~{rec.geometry.bbox_volume_cm3} cm³</span>
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 py-8 sm:px-8">
        {/* Cost headline */}
        <motion.div
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="mb-6 grid gap-4 rounded-sm border border-primary/30 bg-primary/[0.04] p-6 sm:grid-cols-2"
          data-testid="cost-headline"
        >
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Estimated cost / unit</div>
            <div className="mt-1 font-mono text-4xl font-bold tracking-tight text-primary">
              {inr(ce.per_unit_min)} <span className="text-muted-foreground">–</span> {inr(ce.per_unit_max)}
            </div>
          </div>
          <div className="sm:text-right">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              Annual total ({rec.quantity} units/yr)
            </div>
            <div className="mt-1 font-mono text-4xl font-bold tracking-tight">
              {inr(ce.total_min)} <span className="text-muted-foreground">–</span> {inr(ce.total_max)}
            </div>
          </div>
        </motion.div>

        {/* Rate basis */}
        {hasRateBasis && (
          <div className="mb-6 grid gap-3 rounded-sm border border-border bg-card p-4 sm:grid-cols-4" data-testid="rate-basis">
            <Spec label="Part weight" value={rb.part_weight_kg ? `${rb.part_weight_kg} kg` : "—"} />
            <Spec label="Material rate" value={rb.material_rate_inr_per_kg ? `${inr(rb.material_rate_inr_per_kg)} / kg` : "—"} />
            <Spec label="Labour rate" value={rb.labour_rate_inr_per_hr ? `${inr(rb.labour_rate_inr_per_hr)} / hr` : "—"} />
            <Spec label="Machine-hour rate" value={rb.machine_hour_rate_inr ? `${inr(rb.machine_hour_rate_inr)} / hr` : "—"} />
          </div>
        )}

        {/* Batch sensitivity */}
        {sens.length > 0 && (
          <div className="mb-6">
          <Section icon={TrendingDown} title="Batch Sensitivity — Unit Cost vs Volume" testid="section-sensitivity" delay={0.04}>
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Batch</TableHead>
                      <TableHead className="text-right">Batch qty</TableHead>
                      <TableHead className="text-right">Per-unit (min–max)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sens.map((s, i) => (
                      <TableRow key={i} data-testid={`sensitivity-row-${i}`} className={s.label === "1x" ? "bg-primary/[0.05]" : ""}>
                        <TableCell className="font-mono font-medium">{s.label}</TableCell>
                        <TableCell className="text-right font-mono">{s.batch_qty}</TableCell>
                        <TableCell className="text-right font-mono">{inr(s.min)} – {inr(s.max)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                  <LineChart data={sens} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickFormatter={(v) => `₹${v}`} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip formatter={(v) => inr(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }} />
                    <Line type="monotone" dataKey="mid" name="Per-unit cost" stroke="hsl(212 100% 45%)" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Indicative per-unit cost as batch size scales (0.5× → 5× the recommended batch). Larger batches amortise setup &amp; tooling, lowering unit cost.
            </p>
          </Section>
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left column (2/3) */}
          <div className="space-y-6 lg:col-span-2">
            {rec.model_3d_filename && rec.model_3d_file_id && (
              <Section icon={Box} title="3D Part Preview" testid="section-3d" delay={0.03}>
                <ModelViewer analysisId={rec.id} filename={rec.model_3d_filename} />
                <p className="mt-3 font-mono text-xs text-muted-foreground">{rec.model_3d_filename}</p>
              </Section>
            )}
            {/* Material & treatments */}
            <Section icon={Cpu} title="Material & Specifications" testid="section-material" delay={0.05}>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Spec label="Specification" value={a.material?.specification} />
                <Spec label="Grade" value={a.material?.grade} />
                <Spec label="Form" value={a.material?.form} />
                <Spec label="Surface Finish" value={a.surface_finish} />
              </div>
              {a.material?.notes && (
                <p className="mt-3 text-sm text-muted-foreground">{a.material.notes}</p>
              )}
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div className="rounded-sm border border-border p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <Flame className="h-4 w-4 text-chart-3" />
                    <span className="font-title text-sm font-semibold">Heat Treatment</span>
                    <Badge variant="outline" className="rounded-sm text-[10px]">
                      {a.heat_treatment?.required ? "Required" : "Not required"}
                    </Badge>
                  </div>
                  {a.heat_treatment?.required ? (
                    <div className="space-y-1 text-sm">
                      <div><span className="text-muted-foreground">Process: </span>{a.heat_treatment.process || "—"}</div>
                      <div><span className="text-muted-foreground">Spec: </span><span className="font-mono">{a.heat_treatment.specification || "—"}</span></div>
                      <div><span className="text-muted-foreground">Hardness: </span><span className="font-mono">{a.heat_treatment.hardness || "—"}</span></div>
                      {a.heat_treatment.notes && <p className="text-muted-foreground">{a.heat_treatment.notes}</p>}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No heat treatment specified.</p>
                  )}
                </div>
                <div className="rounded-sm border border-border p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-chart-4" />
                    <span className="font-title text-sm font-semibold">Surface Treatment</span>
                    <Badge variant="outline" className="rounded-sm text-[10px]">
                      {a.surface_treatment?.required ? "Required" : "Not required"}
                    </Badge>
                  </div>
                  {a.surface_treatment?.required ? (
                    <div className="space-y-1 text-sm">
                      <div><span className="text-muted-foreground">Process: </span>{a.surface_treatment.process || "—"}</div>
                      <div><span className="text-muted-foreground">Spec: </span><span className="font-mono">{a.surface_treatment.specification || "—"}</span></div>
                      {a.surface_treatment.notes && <p className="text-muted-foreground">{a.surface_treatment.notes}</p>}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No surface treatment specified.</p>
                  )}
                </div>
              </div>
            </Section>

            {/* GD&T */}
            {(a.gdt || []).length > 0 && (
              <Section icon={Ruler} title="GD&T & Tolerances" testid="section-gdt" delay={0.1}>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Feature</TableHead>
                        <TableHead>Characteristic</TableHead>
                        <TableHead className="text-right">Tolerance</TableHead>
                        <TableHead className="text-right">Datum</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {a.gdt.map((g, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{g.feature}</TableCell>
                          <TableCell>{g.characteristic}</TableCell>
                          <TableCell className="text-right font-mono">{g.tolerance}</TableCell>
                          <TableCell className="text-right font-mono">{g.datum || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Section>
            )}

            {/* Key dimensions */}
            {(a.key_dimensions || []).length > 0 && (
              <Section icon={Ruler} title="Key Dimensions" testid="section-dimensions" delay={0.12}>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Feature</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                        <TableHead className="text-right">Tolerance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {a.key_dimensions.map((d, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{d.feature}</TableCell>
                          <TableCell className="text-right font-mono">{d.value}</TableCell>
                          <TableCell className="text-right font-mono">{d.tolerance || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Section>
            )}

            {/* Manufacturing processes */}
            <Section icon={Factory} title="Recommended Manufacturing Route" testid="section-process" delay={0.14}>
              <div className="space-y-4">
                {(a.manufacturing_processes || []).map((p, i) => (
                  <div key={i} className="rounded-sm border border-border p-4">
                    <div className="flex items-center gap-2">
                      <span className="font-title text-base font-semibold">{p.process}</span>
                      {p.primary && (
                        <Badge className="rounded-sm bg-primary text-[10px] uppercase text-primary-foreground">Primary</Badge>
                      )}
                    </div>
                    {p.rationale && <p className="mt-1.5 text-sm text-muted-foreground">{p.rationale}</p>}
                    {(p.steps || []).length > 0 && (
                      <ol className="mt-3 space-y-1.5">
                        {p.steps.map((s, j) => (
                          <li key={j} className="flex gap-2.5 text-sm">
                            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-sm bg-secondary font-mono text-[11px] text-secondary-foreground">
                              {j + 1}
                            </span>
                            <span>{s}</span>
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                ))}
              </div>
            </Section>

            {/* Tooling / Fixtures / Machinery */}
            <div className="grid gap-6 md:grid-cols-2">
              <Section icon={Wrench} title="Tooling" testid="section-tooling" delay={0.16}>
                <ul className="space-y-2.5">
                  {(a.tooling || []).map((t, i) => (
                    <li key={i} className="rounded-sm border border-border p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-title text-sm font-semibold">{t.name}</span>
                        {t.estimated_cost_inr && (
                          <span className="font-mono text-xs text-primary">{t.estimated_cost_inr}</span>
                        )}
                      </div>
                      {t.purpose && <p className="mt-1 text-xs text-muted-foreground">{t.purpose}</p>}
                    </li>
                  ))}
                  {(a.tooling || []).length === 0 && <p className="text-sm text-muted-foreground">None specified.</p>}
                </ul>
              </Section>
              <Section icon={Cog} title="Fixtures" testid="section-fixtures" delay={0.18}>
                <ul className="space-y-2.5">
                  {(a.fixtures || []).map((f, i) => (
                    <li key={i} className="rounded-sm border border-border p-3">
                      <span className="font-title text-sm font-semibold">{f.name}</span>
                      {f.purpose && <p className="mt-1 text-xs text-muted-foreground">{f.purpose}</p>}
                    </li>
                  ))}
                  {(a.fixtures || []).length === 0 && <p className="text-sm text-muted-foreground">None specified.</p>}
                </ul>
              </Section>
            </div>

            <Section icon={Factory} title="Machinery" testid="section-machinery" delay={0.2}>
              <div className="grid gap-3 sm:grid-cols-2">
                {(a.machinery || []).map((m, i) => (
                  <div key={i} className="rounded-sm border border-border p-4">
                    <div className="font-title text-sm font-semibold">{m.name}</div>
                    {m.specification && <div className="mt-0.5 font-mono text-xs text-muted-foreground">{m.specification}</div>}
                    {m.purpose && <p className="mt-1.5 text-xs text-muted-foreground">{m.purpose}</p>}
                  </div>
                ))}
                {(a.machinery || []).length === 0 && <p className="text-sm text-muted-foreground">None specified.</p>}
              </div>
            </Section>

            {(a.quality_inspection || []).length > 0 && (
              <Section icon={ClipboardCheck} title="Quality & Inspection" testid="section-quality" delay={0.22}>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Parameter</TableHead>
                        <TableHead>Method</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {a.quality_inspection.map((q, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{q.parameter}</TableCell>
                          <TableCell>{q.method}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Section>
            )}
          </div>

          {/* Right column (1/3) — cost */}
          <div className="space-y-6">
            {donut.length > 0 && (
              <Section icon={Info} title="Cost Breakdown" testid="section-cost-breakdown" delay={0.1}>
                <ResponsiveContainer width="100%" height={224}>
                  <PieChart>
                    <Pie data={donut} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2} isAnimationActive={false}>
                      {donut.map((d, i) => <Cell key={i} fill={d.color} stroke="hsl(var(--card))" strokeWidth={2} />)}
                    </Pie>
                    <Tooltip formatter={(v) => inr(v)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-2 space-y-1.5">
                  {donut.map((d, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-sm" style={{ background: d.color }} />
                        {d.name}
                      </span>
                      <span className="font-mono text-muted-foreground">{d.percent ? `${d.percent}%` : inr(d.value)}</span>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {regional.length > 0 && (
              <Section icon={MapPin} title="Regional Cost / Unit" testid="section-regional" delay={0.14}>
                <ResponsiveContainer width="100%" height={256}>
                  <BarChart data={regional} layout="vertical" margin={{ left: 8, right: 12 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
                    <XAxis type="number" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} tickFormatter={(v) => `₹${v}`} />
                    <YAxis type="category" dataKey="name" width={70} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => inr(v)} />
                    <Bar dataKey="min" fill="hsl(212 100% 45%)" radius={[0, 2, 2, 0]} name="Min" isAnimationActive={false} />
                    <Bar dataKey="max" fill="hsl(212 100% 70%)" radius={[0, 2, 2, 0]} name="Max" isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-4 space-y-1.5" data-testid="regional-recost-list">
                  {(ce.regional_comparison || []).map((r, i) => {
                    const isCurrent = r.pincode === rec.pincode;
                    const busy = recosting === r.pincode;
                    return (
                      <button
                        key={i}
                        data-testid={`recost-${r.pincode}`}
                        disabled={!!recosting || isCurrent}
                        onClick={() => doRecost(r.pincode, r.region)}
                        className={`flex w-full items-center justify-between gap-2 rounded-sm border px-3 py-2 text-left text-sm transition-colors disabled:opacity-60 ${
                          isCurrent ? "border-primary/40 bg-primary/[0.06]" : "border-border bg-muted/20 hover:border-primary/50 hover:bg-primary/[0.04]"
                        }`}
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <span className="truncate font-medium">{r.region}</span>
                          <span className="shrink-0 font-mono text-xs text-muted-foreground">{r.pincode}</span>
                          {isCurrent && <Badge variant="outline" className="shrink-0 rounded-sm text-[9px] uppercase">current</Badge>}
                        </span>
                        <span className="flex shrink-0 items-center gap-2 font-mono text-xs">
                          {inr(r.per_unit_min)}–{inr(r.per_unit_max)}
                          {!isCurrent && (busy
                            ? <RefreshCw className="h-3.5 w-3.5 animate-spin text-primary" />
                            : <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />)}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Click a hub to re-cost the full report for that region (uses one estimate).
                </p>
              </Section>
            )}

            {(ce.assumptions || []).length > 0 && (
              <Section icon={Info} title="Costing Assumptions" testid="section-assumptions" delay={0.16}>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {ce.assumptions.map((s, i) => (
                    <li key={i} className="flex gap-2"><span className="text-primary">·</span>{s}</li>
                  ))}
                </ul>
              </Section>
            )}

            {(a.manufacturing_risks || []).length > 0 && (
              <Section icon={AlertTriangle} title="Manufacturing Risks" testid="section-risks" delay={0.18}>
                <ul className="space-y-2 text-sm">
                  {a.manufacturing_risks.map((r, i) => (
                    <li key={i} className="flex gap-2">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-chart-3" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}
          </div>
        </div>

        <p className="mt-8 text-center text-xs text-muted-foreground">
          AI-generated estimate for planning purposes. Validate with a supplier quotation before production.
        </p>
      </div>
    </div>
  );
}
